'use client';

import {
  type FC,
  useState,
  useEffect,
  useLayoutEffect,
  useRef,
  ChangeEvent,
} from 'react';
import {
  Button,
  ButtonGroup,
  Flex,
  Icon,
  Select,
  SegmentedControl,
  Input,
  Text,
  TrixEditor,
} from '@/_components';
import { processMessageText } from '@/_utils/strings';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  type DragEndEvent,
} from '@dnd-kit/core';
import {
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { useSortableItem } from '@/_hooks/useSortableItem';
import type {
  DraggableQuestionnaireItemsProps,
  SortableItemProps,
} from './DraggableQuestionnaireItems.types';
import styles from './DraggableQuestionnaireItems.module.css';

// Sortable Item Component for drag-and-drop
const SortableItem: FC<SortableItemProps> = ({
  item,
  updateItem,
  removeItem,
  availableSections,
  isRemoveDisabled = false,
  refs,
}) => {
  const [text, setText] = useState<string>(item.text || '');
  const [selectedSectionId, setSelectedSectionId] = useState<string>('0');

  // Trigger `updateItem` whenever `text` changes
  useEffect(() => {
    if (text !== item.text) {
      // Sanitize text to remove non-breaking spaces from QuillJS bug (temp patch for https://github.com/slab/quill/issues/4535)
      // Also watch: https://github.com/slab/quill/issues/4509
      const sanitizedText = processMessageText(text);
      updateItem(item.id, { text: sanitizedText });
    }
  }, [text, item.text, item.id, updateItem]);

  const onSelectSection = (event: ChangeEvent<HTMLSelectElement>) => {
    const newId: string = event.target.value;
    setSelectedSectionId(newId);
    updateItem(item.id, { section_id: Number(newId) });
  };

  const { attributes, listeners, setNodeRef, style, isDragging } =
    useSortableItem(item.id);

  return (
    <div ref={setNodeRef} style={style}>
      <Flex isRow gap={4} isYCentered>
        <div
          {...attributes}
          {...listeners}
          style={{ cursor: isDragging ? 'grabbing' : 'grab' }}
        >
          <Icon name="grab" size={16} />
        </div>
        <Flex isYCentered={true} gap={4} takeAvailableSpace>
          <Flex ref={refs?.typeRef}>
            <SegmentedControl>
              <SegmentedControl.Item
                title="Question"
                onClick={() => updateItem(item.id, { question_type: 'text' })}
                isActive={item.question_type === 'text'}
              />
              <SegmentedControl.Item
                title="Upload"
                onClick={() => updateItem(item.id, { question_type: 'upload' })}
                isActive={item.question_type === 'upload'}
              />
            </SegmentedControl>
          </Flex>
          {!!availableSections?.length && (
            // Fixed max width to ensure section doesn't overpower item name
            // 27px is the widest possible width without Quill tools wrapping on desktop
            <div style={{ maxWidth: '200px' }}>
              <Flex ref={refs?.sectionRef}>
                <Select
                  placeholder="Addition Items"
                  onChange={onSelectSection}
                  items={availableSections}
                  selectedValue={selectedSectionId}
                  label="Section"
                  isLabelHidden
                  className="max-w-[200px]"
                />
              </Flex>
            </div>
          )}
          <Flex ref={refs?.itemNameRef} isGrowing>
            {item?.question_type === 'text' ? (
              <div className={styles.textEditor}>
                <TrixEditor
                  isDisabled={false}
                  setValue={setText}
                  value={text}
                />
              </div>
            ) : (
              <Input
                label="Item Name"
                isLabelHidden
                placeholder="Enter Item Name"
                onChange={(event) => setText(event.target.value)}
                type="text"
                value={text}
              />
            )}
          </Flex>
        </Flex>
        <Button
          intent="danger-secondary"
          iconLeft="x"
          isIconOnly
          size="tiny"
          tooltip={
            isRemoveDisabled ? 'Cannot remove the only item' : 'Remove item'
          }
          withTooltip
          value="Remove"
          onClick={() => removeItem(item.id)}
          isDisabled={isRemoveDisabled}
        />
      </Flex>
    </div>
  );
};

export const DraggableQuestionnaireItems: FC<
  DraggableQuestionnaireItemsProps
> = ({
  items,
  updateItem,
  removeItem,
  addItem,
  isLoading = false,
  addButtonText = 'New Additional Item',
  availableSections,
  onReorder,
}) => {
  const typeRef = useRef<HTMLDivElement>(null);
  const sectionRef = useRef<HTMLDivElement>(null);
  const itemNameRef = useRef<HTMLDivElement>(null);

  const [columnWidths, setColumnWidths] = useState({
    type: 'auto',
    section: 'auto',
    itemName: 'auto',
  });

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
  );

  // Measure column widths on mount and when availableSections changes for label placement
  useLayoutEffect(() => {
    if (items.length > 0 && typeRef.current) {
      const typeWidth = typeRef.current.offsetWidth;
      const sectionWidth = sectionRef.current?.offsetWidth || 0;

      setColumnWidths({
        type: `${typeWidth}px`,
        section: sectionWidth > 0 ? `${sectionWidth}px` : 'auto',
        itemName: 'auto', // Let this be flexible
      });
    }
  }, [items.length, availableSections]);

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;

    if (active.id !== over?.id) {
      const oldIndex = items.findIndex((item) => item.id === active.id);
      const newIndex = items.findIndex((item) => item.id === over?.id);

      onReorder(oldIndex, newIndex);
    }
  };

  return (
    <Flex isColumn gap={4}>
      {/* Headers Row */}
      <Flex isRow gap={4}>
        <div style={{ width: '24px' }} /> {/* Drag handle spacer */}
        <div style={{ width: columnWidths.type }}>
          <Text type="small" fontWeight="medium">
            Type
          </Text>
        </div>
        {!!availableSections?.length && (
          <div style={{ width: columnWidths.section }}>
            <Text type="small" fontWeight="medium">
              Section
            </Text>
          </div>
        )}
        <div style={{ width: columnWidths.itemName }}>
          <Text type="small" fontWeight="medium">
            Item Name
          </Text>
        </div>
        <div style={{ width: '32px' }} /> {/* X button spacer */}
      </Flex>

      <DndContext
        sensors={sensors}
        collisionDetection={closestCenter}
        onDragEnd={handleDragEnd}
      >
        <SortableContext
          items={items.map((item) => item.id)}
          strategy={verticalListSortingStrategy}
        >
          <Flex isColumn gap={6}>
            {items.map((item, index) => (
              <SortableItem
                key={item.id}
                availableSections={availableSections}
                item={item}
                updateItem={updateItem}
                removeItem={removeItem}
                isRemoveDisabled={items.length === 1}
                refs={
                  index === 0 ? { typeRef, sectionRef, itemNameRef } : undefined
                }
              />
            ))}
          </Flex>
        </SortableContext>
      </DndContext>

      <ButtonGroup
        align="right"
        isSmallGroup={true}
        className={styles.buttonGroup}
      >
        <Button
          value={addButtonText}
          intent="secondary"
          size="small"
          iconLeft="plus"
          type="button"
          onClick={addItem}
          isDisabled={isLoading}
        />
      </ButtonGroup>
    </Flex>
  );
};
